import { useState, useEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import { GET_RENT_CHARGES_REPORT } from '@/graphql/queries/getRentChargesReport';
import {
  PageSizeOption,
  type GetRentChargesReportQuery,
  type GetRentChargesReportQueryVariables,
  type RentChargesReportFilterInput,
} from '@/graphql/generated';
import {
  getRentsThisMonthFromReport,
  type RentsThisMonthBreakdown,
  type RentChargeReportLike,
} from '@/helpers/incomeThisMonth';
import { useDashboardFilter } from '@/contexts/DashboardFilterContext';

type UseRentsThisMonthResult = {
  data: RentsThisMonthBreakdown;
  loading: boolean;
  error: Error | null;
};

type RentChargesReportFilterInputWithProperty = RentChargesReportFilterInput & {
  propertyId?: string;
};

const buildRentChargesFilterInput = (
  pageIndex: number,
  ctx: {
    geoCountryId: string | null;
    geoStateId: string | null;
    geoCityId: string | null;
    selectedPropertyId: string | null;
  },
): RentChargesReportFilterInputWithProperty => {
  const input: RentChargesReportFilterInputWithProperty = {
    pageSize: PageSizeOption.OneHundred,
    page: pageIndex.toString(),
  };
  if (ctx.geoCountryId) input.countryId = ctx.geoCountryId;
  if (ctx.geoStateId) input.stateId = ctx.geoStateId;
  if (ctx.geoCityId) input.cityId = ctx.geoCityId;
  if (ctx.selectedPropertyId) input.propertyId = ctx.selectedPropertyId;
  return input;
};

const computeBreakdownFromItems = (
  items: RentChargeReportLike[],
  periodYear: number | null,
  periodMonth: number | null,
): RentsThisMonthBreakdown => {
  if (periodYear != null && periodMonth != null && periodMonth >= 1 && periodMonth <= 12) {
    return getRentsThisMonthFromReport(items, periodMonth - 1, periodYear);
  }
  if (periodYear != null && periodMonth == null) {
    let charged = 0;
    let notCharged = 0;
    for (let m = 0; m < 12; m++) {
      const b = getRentsThisMonthFromReport(items, m, periodYear);
      charged += b.charged;
      notCharged += b.notCharged;
    }
    return { charged, notCharged, total: charged + notCharged };
  }
  return getRentsThisMonthFromReport(items);
};

export const useRentsThisMonthForDashboard = (): UseRentsThisMonthResult => {
  const client = useApolloClient();
  const {
    geoCountryId,
    geoStateId,
    geoCityId,
    selectedPropertyId,
    periodYear,
    periodMonth,
  } = useDashboardFilter();

  const [data, setData] = useState<RentsThisMonthBreakdown>({
    charged: 0,
    notCharged: 0,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      const allItems: RentChargeReportLike[] = [];
      let page = 1;
      let hasNext = true;

      setLoading(true);
      setError(null);
      try {
        while (hasNext && !cancelled) {
          const result = await client.query<
            GetRentChargesReportQuery,
            GetRentChargesReportQueryVariables
          >({
            query: GET_RENT_CHARGES_REPORT,
            variables: {
              filterInput: buildRentChargesFilterInput(page, {
                geoCountryId,
                geoStateId,
                geoCityId,
                selectedPropertyId,
              }) as GetRentChargesReportQueryVariables['filterInput'],
            },
            fetchPolicy: 'network-only',
          });

          const items = result.data?.rentChargesReport?.items ?? [];
          const pagination = result.data?.rentChargesReport?.pagination;
          allItems.push(...items);
          hasNext = pagination?.hasNextPage ?? false;
          page += 1;
        }

        if (!cancelled) {
          const breakdown = computeBreakdownFromItems(allItems, periodYear, periodMonth);
          setData(breakdown);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchAll();
    return () => {
      cancelled = true;
    };
  }, [
    client,
    geoCountryId,
    geoStateId,
    geoCityId,
    selectedPropertyId,
    periodYear,
    periodMonth,
  ]);

  return { data, loading, error };
};

